import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Theme {
    table: {
      rowHeight: number;
      headerBg: string;
      stripeBg: string;
      hoverBg: string;
      selectedBg: string;
    };
    border: {
      radius: number;
      color: string;
    };
    customColors: {
      appBar: string;
      maroon: string;
      sidePanelSelected: string;
      statusActive: string;
      statusPlanning: string;
      statusOnHold: string;
      statusCompleted: string;
    };
    schedule: {
      /** Committed / booked work — primary sage solid */
      committed: string;
      /** Quote / proposal (tentative) — sage at 60% opacity */
      tentative: string;
      /** Machine time — slate blue */
      machineTime: string;
      /** Employee allocation — moss green */
      employeeAlloc: string;
      /** Maintenance / calibration — amber */
      maintenance: string;
      /** Out of service — grey-amber */
      outOfService: string;
      /** Project envelope row — pale sage outline */
      projectEnvelope: string;
      /** Work order envelope row — slightly darker sage outline */
      workOrderEnvelope: string;
      /** Hard conflict — red */
      conflictHard: string;
      /** Soft conflict — orange */
      conflictSoft: string;
      /** No activity — neutral grey */
      noActivity: string;
      /** Heatmap: 0–70% utilisation — green */
      heatmapLow: string;
      /** Heatmap: 71–100% utilisation — yellow */
      heatmapMid: string;
      /** Heatmap: >100% utilisation — red */
      heatmapHigh: string;
      /** Heatmap: 90–99% utilisation (4-band scheme) — orange */
      heatmapElevated: string;
      /** Today / now marker line — blue */
      nowLine: string;
      /** Today column background tint — blue accent, distinct from the grey weekend tint */
      todayColumn: string;
      /** Selected-booking ring — sage accent */
      selection: string;
      /** Skill level 1 — has not begun training (red) */
      skillLevel1: string;
      /** Skill level 2 — training is ongoing (orange) */
      skillLevel2: string;
      /** Skill level 3 — proficient, can carry out test alone (yellow) */
      skillLevel3: string;
      /** Skill level 4 — mastered, can evaluate others (green) */
      skillLevel4: string;
      /** Task assignment bar — moss green (same as employeeAlloc; named for expand-row strip) */
      taskAssignment: string;
      /** Absence: Vacation — teal */
      absenceVacation: string;
      /** Absence: Sick — rose/pink */
      absenceSick: string;
      /** Absence: FieldWork — deep purple */
      absenceFieldWork: string;
      /** Absence: Other — amber/gold */
      absenceOther: string;
      /**
       * "Today" vertical line on Gantt charts — Material Blue 700.
       * Chosen to contrast with the sage primary palette while remaining
       * recognisable as a "current date" indicator (matches common scheduling
       * conventions — blue = today, red = critical path).
       */
      todayLine: string;
      /**
       * Critical path vertical dashed line — red.
       * Marks the latest-finishing committed task per project.
       */
      criticalPath: string;
      /**
       * Range-selection background for skill matrix cells.
       * Sage at ~12% opacity — visible highlight without washing out the
       * skill-level badge inside the cell.
       */
      selectionBg: string;
      /**
       * Skill level 5 — Trainer (deep green, darker than L4).
       *
       * BUG-2026-0124: the scale is now 1–5 in the DB (CK_SM_Level allows 1–5,
       * migration 077) and SkillLevel in schedule.ts is 1|2|3|4|5. Level 5 =
       * Trainer (mastered, can evaluate others including Reviewer).
       */
      skillLevel5: string;
      /**
       * Skill level 9 — Approver designate (RBAC overhaul Phase C, mig 125).
       * Out-of-band level for the ~4 higher-ups who approve tests outside the
       * system; deep purple so it never reads as part of the 1–5 green ramp.
       */
      skillLevel9: string;
      /**
       * Continuous (24/7) run — Run Mode Phase C (G-3 / BUG-2026-0141).
       * Bar border/marker + legend swatch for tests that run around the clock
       * (e.g. Flex Wall Permeability). Deep sage-teal: sage-family per the
       * locked palette, but clearly distinct from committed sage and the
       * slate-blue machineTime.
       */
      continuous: string;
      /**
       * Employee Weekly Capacity utilization bands (Staffing Allocation %, D5).
       * A person's Σ weekly allocation % maps to one of four bands; these are
       * the band FILL colors (text picks white/dark for contrast in-component):
       *   allocUnder   — 1–49%  under capacity (has room) — calm blue-grey.
       *   allocHealthy — 50–85% comfortably loaded — green.
       *   allocAt      — 86–100% at capacity — amber.
       *   allocOver    — >100%  over-allocated (warn-only, D4) — red.
       */
      allocUnder: string;
      allocHealthy: string;
      allocAt: string;
      allocOver: string;
      /**
       * Skill-fit badges for the "Staff this job" v2 view (STAFF-VIEW-V2.md).
       * A person's skill level for a task's required skill maps to one of these:
       *   skillQualified   — L3+ (proficient / can work alone) — green.
       *   skillMentor      — L1/L2 (needs an L3+ mentor present) — amber.
       *   skillUnqualified — required skill, no level on record — red warning.
       * (No token for "no skill required" — that badge is neutral text.disabled.)
       */
      skillQualified: string;
      skillMentor: string;
      skillUnqualified: string;
      /**
       * "Staff this job" capacity split-bar (redesign 2026-07-14) — the
       * UNCOMMITTED (tentative, other-draft-plan) segment of a person's weekly
       * split availability bar. Amber hatch, distinct from `maintenance`
       * (machine state) even though it's the same amber family. `committed`
       * (sage) and `machineTime` (slate blue, reused for "this project") cover
       * the other two segments.
       */
      capacityUncommitted: string;
      /**
       * Employee Schedule L1 day-load bars (redesign 2026-07-25).
       * weekendWork — teal-slate fill for load on Sat/Sun (a colour no weekday
       * can have, so weekend work pops in one scan).
       * statWorked  — violet fill for load on a stat-holiday date (deliberately
       * far from the >100% orange, which reuses heatmapElevated).
       */
      weekendWork: string;
      statWorked: string;
    };
    /** MoftakiAI chat surface tokens (chat UX modernization v1.0.44). */
    chat: {
      /** Bot identity accent — purple, matches the MoftakiBot icon artwork. */
      botAccent: string;
    };
    /** Inline chart (Highcharts-in-chat) tokens — no hex literals in components. */
    chart: {
      /** Categorical series palette (sage-first, then distinct hues). */
      series: string[];
      /** Grid line colour for chart axes. */
      gridLine: string;
      /** Capacity heatmap: 0% stop — pale sage background. */
      heatLow: string;
      /** Capacity heatmap: mid stop — medium sage. */
      heatMid: string;
    };
    /** Admin-surface tokens (Test Mapping studio — QWO Phase 4). */
    admin: {
      /** Confirmed Test→Catalog mapping edge — solid sage. */
      mappingConfirmed: string;
      /** Unconfirmed (estimated) mapping edge — dashed amber (matches the D4 "unconfirmed" badge). */
      mappingUnconfirmed: string;
      /** Suggested (ghost) mapping edge — dashed slate blue, not yet accepted. */
      mappingSuggestion: string;
      /** Exact-name resolution (no map row needed) — dotted neutral grey. */
      mappingNameMatch: string;
      /** Amber glow ring around unmapped lab-test nodes. */
      unmappedGlow: string;
    };
  }
  interface ThemeOptions {
    table?: {
      rowHeight?: number;
      headerBg?: string;
      stripeBg?: string;
      hoverBg?: string;
      selectedBg?: string;
    };
    border?: {
      radius?: number;
      color?: string;
    };
    customColors?: {
      appBar?: string;
      maroon?: string;
      sidePanelSelected?: string;
      statusActive?: string;
      statusPlanning?: string;
      statusOnHold?: string;
      statusCompleted?: string;
    };
    schedule?: {
      committed?: string;
      tentative?: string;
      machineTime?: string;
      employeeAlloc?: string;
      maintenance?: string;
      outOfService?: string;
      projectEnvelope?: string;
      workOrderEnvelope?: string;
      conflictHard?: string;
      conflictSoft?: string;
      noActivity?: string;
      heatmapLow?: string;
      heatmapMid?: string;
      heatmapHigh?: string;
      heatmapElevated?: string;
      nowLine?: string;
      todayColumn?: string;
      selection?: string;
      skillLevel1?: string;
      skillLevel2?: string;
      skillLevel3?: string;
      skillLevel4?: string;
      taskAssignment?: string;
      absenceVacation?: string;
      absenceSick?: string;
      absenceFieldWork?: string;
      absenceOther?: string;
      todayLine?: string;
      criticalPath?: string;
      skillLevel5?: string;
      skillLevel9?: string;
      selectionBg?: string;
      continuous?: string;
      allocUnder?: string;
      allocHealthy?: string;
      allocAt?: string;
      allocOver?: string;
      skillQualified?: string;
      skillMentor?: string;
      skillUnqualified?: string;
      capacityUncommitted?: string;
      weekendWork?: string;
      statWorked?: string;
    };
    chat?: {
      botAccent?: string;
    };
    chart?: {
      series?: string[];
      gridLine?: string;
      heatLow?: string;
      heatMid?: string;
    };
    admin?: {
      mappingConfirmed?: string;
      mappingUnconfirmed?: string;
      mappingSuggestion?: string;
      mappingNameMatch?: string;
      unmappedGlow?: string;
    };
  }
}

export const theme = createTheme({
  palette: {
    primary: {
      main: '#4A6741',
    },
    secondary: {
      main: '#8B7355',
    },
    background: {
      default: '#EEF2E6',
      paper: '#FFFFFF',
    },
    error: {
      main: '#C62828',
    },
  },
  typography: {
    fontFamily: "'Inter', 'Roboto', sans-serif",
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
  },
  spacing: 8,
  table: {
    rowHeight: 40,
    headerBg: '#E8EDE4',
    stripeBg: '#F5F7F3',
    hoverBg: '#E8EDE4',
    selectedBg: '#DBEAFE',
  },
  border: {
    radius: 4,
    color: '#C5CEC0',
  },
  customColors: {
    appBar: '#3E4A3E',
    maroon: '#8B1A1A',
    sidePanelSelected: '#D6DDD2',
    statusActive: '#4CAF50',
    statusPlanning: '#FFC107',
    statusOnHold: '#FF9800',
    statusCompleted: '#9E9E9E',
  },
  schedule: {
    committed: '#4A6741',
    tentative: '#7A9E76',
    machineTime: '#546E96',
    employeeAlloc: '#4D7A4D',
    maintenance: '#D4860A',
    outOfService: '#8D6E63',      // grey-brown — visually distinct from amber maintenance
    projectEnvelope: '#A8C5A0',   // pale sage — thin outer bar colour
    workOrderEnvelope: '#7A9E76', // medium sage — slightly darker than projectEnvelope
    conflictHard: '#C62828',
    conflictSoft: '#E65100',
    noActivity: '#9E9E9E',
    heatmapLow: '#4CAF50',
    heatmapMid: '#FFC107',
    heatmapElevated: '#FB8C00',  // Material Orange 600 — 4-band 90–99% band
    heatmapHigh: '#C62828',
    nowLine: '#1E6FD9',
    todayColumn: '#1E6FD9',  // Today column tint — same blue as the now-line, so "today" reads blue (not grey like weekends)
    selection: '#4A6741',
    // Skill level traffic-light colors (per mockup 03.1 EmployeeMatrixUpdate.png)
    skillLevel1: '#C62828',  // 1 = Has not begun training — red
    skillLevel2: '#E65100',  // 2 = Training is ongoing — orange
    skillLevel3: '#F9A825',  // 3 = Proficient, can work alone — yellow/amber
    skillLevel4: '#388E3C',  // 4 = Mastered, can evaluate others — green
    // Expand-row Gantt strip bar colors (Phase 3, Item 3)
    taskAssignment:   '#4D7A4D',  // Moss green (same as employeeAlloc)
    absenceVacation:  '#0288D1',  // Teal-blue — matches mockup "Field Work – Sampling" green
    absenceSick:      '#D81B60',  // Rose/pink — visually distinct for sick leave
    absenceFieldWork: '#558B2F',  // Olive green — field work out of office
    absenceOther:     '#F9A825',  // Amber — "Internal Review & Logging" (mockup yellow bar)
    todayLine:        '#1976D2',  // Material Blue 700 — "Today" vertical line on Gantt
    criticalPath:     '#C62828',  // Red dashed line — critical path (latest-finishing committed task)
    // NOTE: Phase 1 defined SkillLevel as 1|2|3|4 (DB constraint).
    // BUG-2026-0124: skill scale expanded to 1–5. Level 5 = Trainer.
    skillLevel5:      '#1B5E20',  // Dark green — Trainer (darker than L4 #388E3C)
    skillLevel9:      '#5E35B1',  // Deep purple — Approver designate (RBAC Phase C; distinct from the 1–5 ramp)
    // Range-selection background — sage primary at ~12% opacity (#4A6741 + 1F alpha)
    selectionBg:      'rgba(74, 103, 65, 0.12)',
    // Continuous (24/7) runs — deep sage-teal, distinct from committed sage +
    // slate-blue machineTime (Run Mode Phase C).
    continuous:       '#2F6F5E',
    // Employee Weekly Capacity utilization bands (Staffing Allocation %, D5).
    allocUnder:       '#5C8AA6',  // Calm blue-grey — under capacity (has room)
    allocHealthy:     '#4CAF50',  // Green — comfortably loaded (matches heatmapLow)
    allocAt:          '#E5A100',  // Amber — at capacity (deeper than heatmapMid for white-on contrast)
    allocOver:        '#C62828',  // Red — over capacity (warn-only; matches heatmapHigh)
    // Skill-fit badges for the "Staff this job" v2 view (STAFF-VIEW-V2.md).
    skillQualified:   '#2E7D32',  // Green — L3+ qualified (can work alone)
    skillMentor:      '#E5A100',  // Amber — L1/L2 needs an L3+ mentor (matches allocAt)
    skillUnqualified: '#C62828',  // Red — required skill with no level on record (warning)
    // "Staff this job" capacity split-bar (redesign 2026-07-14) — uncommitted
    // (other-draft) segment. Amber, matching the approved mockup's --tent (#C79114).
    capacityUncommitted: '#C79114',
    // Employee Schedule L1 day-load bars (redesign 2026-07-25).
    weekendWork: '#46788A',  // Teal-slate — weekend load, unique vs weekday green
    statWorked:  '#7B5EA7',  // Violet — load on a stat holiday, far from over-100 orange
  },
  // MoftakiAI chat (chat UX modernization). The purple bot accent predates the
  // token — it matches the MoftakiBot icon artwork and reads "AI", distinct
  // from the sage app palette.
  chat: {
    botAccent: '#7C4DFF',
  },
  // Inline charts rendered inside the AI chat (Highcharts). Sage-first series
  // palette; the heatmap ramp mirrors the schedule capacity semantics.
  chart: {
    series: ['#4A6741', '#8B7355', '#546E96', '#1565C0', '#6A1B9A', '#C62828', '#D4860A', '#26A69A', '#8FAF85'],
    gridLine: '#F0F0F0',
    heatLow: '#EEF2E6',
    heatMid: '#8FAF85',
  },
  // Admin → Test Mapping studio (QWO Phase 4). Edge/badge semantics follow the
  // schedule palette: sage = trusted/committed, amber = needs attention.
  admin: {
    mappingConfirmed:   '#4A6741',  // solid sage — lab-blessed mapping
    mappingUnconfirmed: '#D4860A',  // dashed amber — estimate awaiting the lab (D4)
    mappingSuggestion:  '#546E96',  // dashed slate blue — fuzzy-match ghost edge
    mappingNameMatch:   '#9E9E9E',  // dotted grey — resolves by exact name, no row
    unmappedGlow:       '#D4860A',  // amber glow on unmapped test nodes
  },
  components: {
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: '#4A6741',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#3D5636',
          },
        },
      },
    },
  },
});
